import { ChevronDown, ExternalLink } from 'lucide-react'
import type { UpcomingItem, UpcomingStatus } from '@manaro/contracts'
import { Badge, type BadgeTone } from '@/shared/ui/badge.tsx'
import { Progress } from '@/shared/ui/progress.tsx'
import { cn } from '@/shared/lib/cn.ts'

const STATUS: Record<UpcomingStatus, { tone: BadgeTone; dot: string }> = {
  'В разработке': { tone: 'brand', dot: 'bg-brand' },
  'На тестировании': { tone: 'info', dot: 'bg-info-dot' },
  'В плане': { tone: 'neutral', dot: 'bg-ink-faint' },
}

interface UpcomingRowProps {
  item: UpcomingItem
  open: boolean
  onToggle: () => void
}

export function UpcomingRow({ item, open, onToggle }: UpcomingRowProps) {
  const status = STATUS[item.status]
  const facts = [
    { label: 'Сейчас', value: item.stage },
    { label: 'Команда', value: item.team },
    { label: 'Затронет', value: item.affects },
  ].filter((fact): fact is { label: string; value: string } => Boolean(fact.value))

  return (
    <div className="border-b border-dashed border-[#ede6d8] last:border-b-0">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full flex-wrap items-start gap-x-4 gap-y-2 py-4 text-left md:flex-nowrap"
      >
        <div className="basis-full pt-0.5 text-[12.5px] text-ink-muted md:basis-24 md:shrink-0">
          {item.when ?? 'срок уточняется'}
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <div className="flex flex-wrap items-center gap-2.5">
            <span className="text-[16px] font-medium tracking-[-0.005em]">{item.title}</span>
            <Badge tone={status.tone}>
              <span className={cn('size-[5px] rounded-full', status.dot)} />
              {item.status}
            </Badge>
            {item.areas.map((area) => (
              <Badge key={area} tone="outline" className="font-medium">
                {area}
              </Badge>
            ))}
          </div>

          {item.summary && <span className="text-[14px] text-ink-muted text-balance-pretty">{item.summary}</span>}

          {item.progress !== null && (
            <div className="mt-0.5 flex items-center gap-2.5">
              <Progress value={item.progress} className="max-w-[260px] flex-1" trackClassName="h-[5px]" />
              <span className="tabular text-[12px] text-ink-muted">{item.progress}%</span>
            </div>
          )}
        </div>

        <ChevronDown
          className={cn('mt-0.5 size-4 shrink-0 text-ink-muted transition-transform duration-200', open && 'rotate-180')}
        />
      </button>

      {open && (
        <div className="flex flex-col gap-3 pb-5 md:pl-[112px]">
          {item.scope.length > 0 && (
            <div className="rounded-control border border-line-soft bg-surface px-4 py-3.5">
              <div className="mb-2.5 text-[12px] font-semibold tracking-[0.06em] text-ink-muted uppercase">Что войдёт</div>
              <ul className="flex flex-col gap-1.5">
                {item.scope.map((line) => (
                  <li key={line} className="flex gap-2.5 text-[14px] text-ink-soft text-balance-pretty">
                    <span className="mt-[7px] size-[5px] shrink-0 rounded-full bg-[#b9c7bc]" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {facts.length > 0 && (
            <div className="flex flex-wrap gap-x-7 gap-y-2.5">
              {facts.map((fact) => (
                <div key={fact.label} className="flex min-w-0 flex-col gap-0.5">
                  <span className="text-[11.5px] tracking-[0.05em] text-ink-muted uppercase">{fact.label}</span>
                  <span className="text-[13.5px] text-ink-soft">{fact.value}</span>
                </div>
              ))}
            </div>
          )}

          <a
            href={item.source.url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-[12.5px] text-ink-muted hover:text-brand"
          >
            <ExternalLink className="size-3.5" />
            {item.source.repo} #{item.source.number}
          </a>
        </div>
      )}
    </div>
  )
}
